<template>
  <v-card
    class="d-flex flex-wrap align-center pa-4"
  >
    <div class="d-flex align-center">
      <back-button @close-event="closeDialog" />
      <page-header>
        {{ $t('billDetail') }} · #{{ this.info.billNr }}
      </page-header>
    </div>
    <div
      class="d-flex"
    >
      <div>
        <v-card
          class="pr-4 mt-2"
          outlined
        >
          <div
            class="text-h4 pa-4 pl-9"
          >
            <v-icon
              class="mr-2"
            >
              mdi-timeline
            </v-icon>

            {{ $t('mealsDetail') }}
          </div>
          <v-divider />
          <v-timeline
            align-top
            dense
          >
            <v-timeline-item
              color="amber"
              small
            >
              <div class="d-flex pr-4">
                <div>{{ $t('startMeal') }}</div>
                <v-spacer />
                {{ basicInfo.startTime | beautifulTime }}
              </div>
            </v-timeline-item>
            <template v-if="displayDetailInfo">
              <template v-for="t in Object.keys(displayDetailInfo)">
                <v-timeline-item
                  :key="t"
                  small
                >
                  <div>
                    <div
                      v-for="op in Object.keys(displayDetailInfo[t])"
                      :key="t+'op'+op"
                    >
                      <div class="d-flex pr-4">
                        <div> {{ $t(op) }}</div>
                        <v-spacer />
                        {{ t | beautifulTime }}
                      </div>
                      <div class="mt-4">
                        <v-card
                          v-for="d in displayDetailInfo[t][op]"
                          :key="d.id"
                          class="pa-2 ml-0 ma-1 text-body-1"
                          color="#eee"
                          elevation="0"
                        >
                          <div>
                            <div class="d-flex">
                              <b>{{ Math.abs(d.count) }}&times; </b>
                              <span class="text-capitalize text-no-wrap text-truncate ml-2"> #{{
                                d.currentCode
                              }} </span>
                              <v-spacer />
                              <template v-if="parseFloat(d.tempDiscountMod)!==0">
                                <span
                                  class="text-decoration-line-through warning--text text--darken-1"
                                  style="font-size: 10px"
                                >{{ d.price | priceDisplay }}</span>
                                <span class="text-no-wrap">
                                  {{ parseFloat(d.price) + parseFloat(d.tempDiscountMod) | priceDisplay }}</span>
                              </template>
                              <span
                                v-else
                                class="text-no-wrap"
                              >{{ d.price | priceDisplay }}</span>
                            </div>
                            <div class="d-flex">
                              <span class="maxLine2">{{ d.name }}</span>
                              <v-spacer />
                              <div>{{ d.servantName }}</div>
                            </div>
                          </div>
                          <template v-if="d.aName">
                            <div class="text-caption">
                              {{ d.aName }}
                            </div>
                          </template>
                        </v-card>
                      </div>
                    </div>
                  </div>
                </v-timeline-item>
              </template>
            </template>
            <v-timeline-item
              v-if="basicInfo.paymentStatus === '1'"
              color="success"
              small
            >
              <div class="d-flex pr-4">
                <div>{{ $t('endMeal') }}</div>
                <v-spacer />
                {{ basicInfo.endTime | beautifulTime }}
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card>
        <v-card
          class="pa-4 px-8 mt-2 font-weight-regular"
          elevation="0"
          outlined
        >
          <table
            class="mt-6"
            style="width: 100%"
          >
            <tbody>
              <tr>
                <td style="width: 140px">
                  {{ $t('subtotal') }}
                </td>
                <td style="width: 140px">
                  {{ $t('numberOfGoods', { numberOfGoods: count }) }}
                </td>
                <td style="text-align: right">
                  {{ dishTotalPrice | priceDisplay }}
                </td>
              </tr>
              <tr v-if="basicInfo.tipIncome>0">
                <td style="width: 140px">
                  {{ $t('tips') }}
                </td>
                <td style="width: 140px" />
                <td style="text-align: right">
                  {{ basicInfo.tipIncome | priceDisplay }}
                </td>
              </tr>
              <tr v-if="basicInfo.discountStr">
                <td style="width: 140px">
                  {{ $t('discount') }}
                </td>
                <td style="width: 140px">
                  {{ $t('totalPrice') }}
                </td>
                <td style="text-align: right">
                  {{ basicInfo.discountStr }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td style="width: 140px">
                  {{ $t('totalPrice') }}
                </td>
                <td />

                <td style="text-align: right">
                  {{ totalPrice | priceDisplay }}
                </td>
              </tr>
            </tbody>
          </table>
          <v-divider class="my-4" />
          <div class="text-subtitle-2 font-weight-regular">
            {{ $t('userPay') }}
          </div>
          <v-card
            class="mt-2"
            elevation="0"
          >
            <div
              v-for="item in paymentRecordList"
              :key="item.id"
              class="d-flex py-2"
              style="font-size: 16px "
            >
              <div
                class="amber mr-6 px-2 white--text d-flex align-center justify-center"
                style="border-radius: 8px"
              >
                {{ item.name }}
              </div>
              <v-spacer />
              {{ item.amount | priceDisplay }}
            </div>
          </v-card>
        </v-card>
      </div>
      <div
        :class="!$vuetify.breakpoint.mdAndUp ? 'flex-grow-1 order-0 mt-2' : 'ml-8 mt-2 flex-shrink-0'"
        :style="!$vuetify.breakpoint.mdAndUp ? 'position: relative; width: 100%;' : 'position: sticky; top: 0; width: 300px;'"
      >
        <v-card
          class="py-4"
          elevation="0"
          outlined
        >
          <v-card
            elevation="0"
          >
            <div class="text-h4 px-4">
              {{ $t('Information') }}
            </div>
            <div class="mt-8 px-4 text-body-1 font-weight-regular">
              <div
                class="text-h4"
              >
                <v-icon
                  v-if="basicInfo.paymentStatus==='1'"
                  class="mr-2"
                  color="success"
                >
                  mdi-check-decagram
                </v-icon>
                <v-icon
                  v-else
                  class="mr-2"
                  color="error"
                >
                  mdi-close-octagon-outline
                </v-icon>
                {{ paymentStatus }}
              </div>
            </div>
            <v-divider class="my-4" />
            <div class="px-4">
              <div class="text-overline font-weight-bold">
                {{ $t('staff') }} / {{ $t('tableNumber') }}
              </div>
              <div class="mt-2 text-body-1 font-weight-regular">
                {{ basicInfo.servantName }} / {{ showDailyNumber ? dailyNumber : basicInfo.tableName }}
              </div>
            </div>
            <div class="px-4 mt-4">
              <div class="text-overline font-weight-bold">
                {{ $t('Customers') }}
              </div>
              <div class="mt-2 text-body-1 font-weight-regular">
                {{ basicInfo.personCount }}
              </div>
            </div>
            <div class="px-4 mt-4">
              <div class="text-overline font-weight-bold">
                {{ $t('OrderType') }}
              </div>
              <div class="mt-2 text-body-1 font-weight-regular">
                {{ consumeType }}
              </div>
            </div>
            <!-- 快餐扫码单堂食（consumeTypeId 1）也要显示：桌号只有堂食单才有（POS-239） -->
            <template v-if="rawAddressInfo && (isToGoType || isQuickOrder)">
              <v-divider class="my-4" />
              <div class="px-4">
                <div class="d-flex">
                  <div class="text-overline">
                    {{ $t('address_info') }}
                  </div>
                  <v-spacer />
                  <div v-if="showDetailBtn">
                    <v-btn
                      class="text-overline"
                      small
                      text
                      @click="showAddressDetailInfo = true"
                    >
                      {{ $t('Details') }}
                    </v-btn>
                  </div>
                </div>

                <div class="mt-2 text-body-1 font-weight-regular">
                  <address-info-block
                    :address-info="rawAddressInfo"
                    :external-id="basicInfo?.externalId ?? ''"
                  />
                </div>
              </div>
            </template>
          </v-card>
        </v-card>
      </div>
    </div>
    <v-dialog
      v-model="showAddressDetailInfo"
      max-width="600px"
    >
      <v-card class="pa-4">
        <div>
          <div class="d-flex align-center justify-center">
            <div>
              {{ $t('address_info') }}
            </div>
            <v-spacer />
            <v-btn
              icon
              @click="showAddressDetailInfo = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="mt-2 text-body-1 font-weight-regular">
            <json-viewer
              :value="rawAddressInfo"
              copyable
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>

import PageHeader from '@/views/widget/typography/PageHeader'
import { groupBy } from 'lodash'
import BackButton from '@/views/widget/uiComponent/BackButton.vue'
import AddressInfoBlock from '@/views/widget/commonLogic/base/AddressInfoBlock.vue'
import JsonViewer from 'vue-json-viewer'
import GlobalConfig from '@/model/global/GlobalSettings'

export default {
  name: 'DetailOrder',
  components: {
    AddressInfoBlock,
    BackButton,
    PageHeader,
    JsonViewer
  },
  props: {
    info: {}
  },
  data () {
    return {
      showAddressDetailInfo: false,
      orders: []
    }
  },
  computed: {
    showDetailBtn () {
      return GlobalConfig.mode === 'Admin'
    },
    rawAddressInfo () {
      return this.basicInfo.rawAddressInfo ? JSON.parse(this.basicInfo.rawAddressInfo) : ''
    },
    consumeType () { return this.basicInfo.consumeTypeId },
    showDailyNumber () {
      return this.info?.restaurantInfo?.useDailySerialAsQueueNumber === '1' && (this.basicInfo.tableName.startsWith('W') || this.basicInfo.tableName.startsWith('w'))
    },
    paymentStatus () {
      return this.basicInfo.paymentStatus === '1'
        ? this.$i18n.t('paid')
        : this.$i18n.t(
          'not_paid')
    },
    count () {
      return this.orders.reduce((sum, i) => sum + parseInt(i.sumCount), 0)
    },
    paymentRecordList () {
      return this.info.billPaymentInfo.map(item => {
        item.createdAt = item.createdAt ?? ''
        return item
      }) ?? []
    },
    totalPrice () {
      return parseFloat(this.basicInfo.totalPrice ?? 0)
    },
    dishTotalPrice () {
      return this.orders.reduce((sum, i) => sum + parseFloat(i.totalPrice), 0)
    },
    detailedOrders () {
      return (this.info?.orderDetailInfo ?? []).map(o => {
        let name
        switch (o.dishStatus) {
          case '-1':
            name = 'returnDish'
            break
          case '-100':
            name = 'dividedOrder'
            break
          case '-101':
            name = 'tableChanged'
            break
          default:
            name = 'orderFood'
        }
        o.dishStatusName = name
        return o
      })
    },
    displayDetailInfo () {
      const groupedInfo = groupBy(this.detailedOrders, 'time')
      for (const time in groupedInfo) {
        groupedInfo[time] = groupBy(groupedInfo[time], 'dishStatusName')
        for (const key in groupedInfo[time]) {
          const timeInfo = groupBy(groupedInfo[time][key], (i) => {
            return i.currentCode + i.name + i.note + i.aName + i.tempDiscountStr
          })
          groupedInfo[time][key] = Object.keys(timeInfo).reduce((arr, i) => {
            arr.push(timeInfo[i].reduce((obj, t) => {
              obj.count = parseInt(obj.count) + parseInt(t.count)
              return obj
            }))
            return arr
          }, [])
        }
      }
      return groupedInfo
    },
    dailyNumber () {
      return this.basicInfo?.dailySerial ? 'W' + this.basicInfo?.dailySerial : this.basicInfo?.tableName
    },
    isToGoType () {
      return this.basicInfo.consumeTypeId.toString() === '2'
    },
    isQuickOrder () {
      return String(this.basicInfo?.externalId ?? '').startsWith('q_')
    },
    basicInfo () {
      return this.info?.billInfo
    }
  },
  async mounted () {
    await this.reload()
  },
  methods: {
    closeDialog () {
      this.$emit('close')
    },
    async reload () {

    }
  }
}
</script>

<style scoped>

</style>
